<template>
  <v-container fluid>
    <v-card class="pa-5 mt-3">
      <v-card-title>
        <h3>{{ $t("statistics.year_comparison") }}</h3>
      </v-card-title>

      <!-- Year Selection Controls -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-select
                v-model="selectedOldYear"
                :items="years"
                item-text="school_year"
                item-value="id"
                :label="$t('statistics.old_year')"
                outlined
                dense
                @change="onYearChange"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
                v-model="selectedNewYear"
                :items="years"
                item-text="school_year"
                item-value="id"
                :label="$t('statistics.new_year')"
                outlined
                dense
                @change="onYearChange"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Loading Indicator -->
      <div class="text-center ma-12" v-if="loading">
        <v-progress-circular size="64" indeterminate color="primary" width="6"/>
      </div>

      <!-- Statistics Table -->
      <div v-else-if="comparisonDataWithTotals && comparisonDataWithTotals.length > 0 && selectedOldYear && selectedNewYear">
        <v-card-text>
          <!-- Export Button -->
          <v-row class="mb-4">
            <v-col cols="12" class="text-right">
              <vue-excel-xlsx
                  :data="exportData"
                  :columns="exportColumns"
                  :file-name="'مقارنة-السنوات-' + (new Date()).toLocaleDateString()"
                  :file-type="'xlsx'"
                  :sheet-name="'sheet1'"
              >
                <v-btn color="primary">
                  {{ $t("export") }}
                  <v-icon class="ms-2">mdi-file-excel</v-icon>
                </v-btn>
              </vue-excel-xlsx>
            </v-col>
          </v-row>

          <!-- Data Table -->
          <v-data-table
              :headers="headers"
              :items="comparisonDataWithTotals"
              :items-per-page="-1"
              hide-default-footer
              class="elevation-1 bordered"
              :sort-desc="[false]"
          >
            <template v-slot:item.governorate_name="{ item }">
              <span :class="{ 'font-weight-bold': item.isTotal }">
                {{ item.governorate_name }}
              </span>
            </template>

            <template v-slot:item.directorate_name="{ item }">
              <span :class="{ 'font-weight-bold': item.isTotal }">
                {{ item.directorate_name }}
              </span>
            </template>

            <template v-slot:item.schools_in_old_year="{ item }">
              <v-chip
                  small
                  :color="item.statistics.schools_in_old_year > 0 ? 'blue' : 'grey'"
                  dark
                  @click="openDialog('schools_in_old_year', item)"
                  :class="!item.isTotal ? 'clickable-chip' : ''"
              >
                {{ item.statistics.schools_in_old_year }}
              </v-chip>
            </template>

            <template v-slot:item.schools_in_new_year="{ item }">
              <div class="text-center">
                <v-chip
                    small
                    :color="item.statistics.schools_in_new_year > 0 ? 'green' : 'grey'"
                    dark
                    @click="openDialog('schools_in_new_year', item)"
                    :class="!item.isTotal ? 'clickable-chip' : ''"
                >
                  {{ item.statistics.schools_in_new_year }}
                </v-chip>
                <v-chip
                    x-small
                    :color="getPercentageChipColor(item.percentages.schools_in_new_year)"
                    dark
                    label
                    class="mr-1"
                >
                  {{ formatPercentage(item.percentages.schools_in_new_year) }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.new_schools_count="{ item }">
              <div class="text-center">
                <v-chip
                    small
                    :color="item.statistics.new_schools_count > 0 ? 'orange' : 'grey'"
                    dark
                    @click="openDialog('new_schools_count', item)"
                    :class="!item.isTotal ? 'clickable-chip' : ''"
                >
                  {{ item.statistics.new_schools_count }}
                </v-chip>
                <v-chip
                    x-small
                    :color="getPercentageChipColor(item.percentages.new_schools_count)"
                    dark
                    label
                    class="mr-1"
                >
                  {{ formatPercentage(item.percentages.new_schools_count) }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.renewal_requests_pending="{ item }">
              <div class="text-center">
                <v-chip
                    small
                    :color="item.statistics.renewal_requests_pending > 0 ? 'amber' : 'grey'"
                    dark
                    @click="openDialog('renewal_requests_pending', item)"
                    :class="!item.isTotal ? 'clickable-chip' : ''"
                >
                  {{ item.statistics.renewal_requests_pending }}
                </v-chip>
                <v-chip
                    x-small
                    :color="getPercentageChipColor(item.percentages.renewal_requests_pending)"
                    dark
                    label
                    class="mr-1"
                >
                  {{ formatPercentage(item.percentages.renewal_requests_pending) }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.renewal_requests_accepted="{ item }">
              <div class="text-center">
                <v-chip
                    small
                    :color="item.statistics.renewal_requests_accepted > 0 ? 'success' : 'grey'"
                    dark
                    @click="openDialog('renewal_requests_accepted', item)"
                    :class="!item.isTotal ? 'clickable-chip' : ''"
                >
                  {{ item.statistics.renewal_requests_accepted }}
                </v-chip>
                <v-chip
                    x-small
                    :color="getPercentageChipColor(item.percentages.renewal_requests_accepted)"
                    dark
                    label
                    class="mr-1"
                >
                  {{ formatPercentage(item.percentages.renewal_requests_accepted) }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.renewed_schools="{ item }">
              <div class="text-center">
                <v-chip
                    small
                    :color="item.statistics.renewed_schools > 0 ? 'success' : 'grey'"
                    dark
                    @click="openDialog('renewed_schools', item)"
                    :class="!item.isTotal ? 'clickable-chip' : ''"
                >
                  {{ item.statistics.renewed_schools }}
                </v-chip>
                <v-chip
                    x-small
                    :color="getPercentageChipColor(item.percentages.renewed_schools)"
                    dark
                    label
                    class="mr-1"
                >
                  {{ formatPercentage(item.percentages.renewed_schools) }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.renewal_requests_rejected="{ item }">
              <div class="text-center">
                <v-chip
                    small
                    :color="item.statistics.renewal_requests_rejected > 0 ? 'error' : 'grey'"
                    dark
                    @click="openDialog('renewal_requests_rejected', item)"
                    :class="!item.isTotal ? 'clickable-chip' : ''"
                >
                  {{ item.statistics.renewal_requests_rejected }}
                </v-chip>
                <v-chip
                    x-small
                    :color="getPercentageChipColor(item.percentages.renewal_requests_rejected)"
                    dark
                    label
                    class="mr-1"
                >
                  {{ formatPercentage(item.percentages.renewal_requests_rejected) }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.schools_not_requested_renewal="{ item }">
              <div class="text-center">
                <v-chip
                    small
                    :color="item.statistics.schools_not_requested_renewal > 0 ? 'error' : 'grey'"
                    dark
                    @click="openDialog('schools_not_requested_renewal', item)"
                    :class="!item.isTotal ? 'clickable-chip' : ''"
                >
                  {{ item.statistics.schools_not_requested_renewal }}
                </v-chip>
                <v-chip
                    x-small
                    :color="getPercentageChipColor(item.percentages.schools_not_requested_renewal)"
                    dark
                    label
                    class="mr-1"
                >
                  {{ formatPercentage(item.percentages.schools_not_requested_renewal) }}
                </v-chip>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="text-center ma-12">
                <v-icon size="64" color="grey">mdi-chart-line</v-icon>
                <p class="text-h6 grey--text mt-4">{{ $t('statistics.no_data_found') }}</p>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </div>

      <!-- No Data Message -->
      <div v-else-if="!loading" class="text-center ma-12">
        <v-icon size="64" color="grey">mdi-chart-line</v-icon>
        <p class="grey--text mt-4">{{ $t('statistics.select_years_to_compare') }}</p>
      </div>
    </v-card>

    <!-- Schools Data Dialog -->
    <v-dialog v-model="schoolsDialog" max-width="1200" scrollable>
      <v-card>
        <v-card-title>
          {{ currentDialogTitle }}
          <v-spacer></v-spacer>
          <vue-excel-xlsx
              v-if="currentSchoolsData.length > 0"
              :data="currentSchoolsData"
              :columns="schoolExportColumns"
              :file-name="currentDialogTitle + '-' + (new Date()).toLocaleDateString()"
              :file-type="'xlsx'"
              :sheet-name="'sheet1'"
          >
            <v-btn color="primary" class="ml-2">
              {{ $t("export") }}
              <v-icon class="ms-2">mdi-file-excel</v-icon>
            </v-btn>
          </vue-excel-xlsx>
          <v-btn icon @click="schoolsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="text-center ma-12" v-if="dialogLoading">
            <v-progress-circular size="64" indeterminate color="primary" width="6"/>
          </div>
          <div v-else-if="currentSchoolsData.length === 0" class="text-center ma-12">
            <v-icon size="64" color="grey">mdi-school-outline</v-icon>
            <p class="grey--text mt-4">{{ $t('statistics.no_schools_found') }}</p>
          </div>
          <v-data-table
              v-else
              :headers="schoolHeaders"
              :items="currentSchoolsData"
              :loading="dialogLoading"
              :items-per-page="-1"
              hide-default-footer
              class="elevation-1"
          >
            <template v-slot:item.owner="{ item }">
              <v-chip
                  :color="item.owner === 'private_schools' ? 'orange' : 'blue'"
                  dark
                  small
              >
                {{ item.owner === 'private_schools' ? $t('schools.private_schools') : $t('schools.public_schools') }}
              </v-chip>
            </template>

            <template v-slot:no-data>
              <div class="text-center ma-12">
                <v-icon size="64" color="grey">mdi-school-outline</v-icon>
                <p class="grey--text mt-4">{{ $t('statistics.no_schools_found') }}</p>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'YearComparison',
  data() {
    return {
      loading: false,
      dialogLoading: false,
      years: [],
      selectedOldYear: null,
      selectedNewYear: null,
      comparisonData: [],
      currentSchoolsData: [],
      schoolsDialog: false,
      currentDialogTitle: '',
      currentDirectorate: null,
    };
  },
  computed: {
    comparisonDataWithTotals() {
      if (!this.comparisonData || this.comparisonData.length === 0) {
        return [];
      }

      // Calculate percentages for each row
      const dataWithPercentages = this.comparisonData.map(item => {
        const percentages = this.calculatePercentages(item.statistics);
        return {
          ...item,
          percentages
        };
      });

      // Calculate totals
      const totals = this.calculateTotals(this.comparisonData);
      const totalPercentages = this.calculatePercentages(totals);

      // Add totals row
      const totalsRow = {
        governorate_name: this.$t('statistics.total') || 'Total',
        directorate_name: null,
        directorate_id: null,
        statistics: totals,
        percentages: totalPercentages,
        isTotal: true
      };

      return [totalsRow, ...dataWithPercentages];
    },

    headers() {
      return [
        {
          text: this.$t('directorates.governorate'),
          value: 'governorate_name',
          width: 150,
          sortable: true,
          class: 'fixed',
          cellClass: 'fixed'
        },
        {
          text: this.$t('directorates.directorate'),
          value: 'directorate_name',
          width: 200,
          sortable: true,
          class: 'fixed',
          cellClass: 'fixed'
        },
        {
          text: this.$t('statistics.schools_in_old_year'),
          value: 'schools_in_old_year',
          width: 130,
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('statistics.renewed_schools'),
          value: 'renewed_schools',
          width: 150,
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('statistics.new_schools'),
          value: 'new_schools_count',
          width: 150,
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('statistics.schools_in_new_year'),
          value: 'schools_in_new_year',
          width: 150,
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('statistics.renewal_requests_pending'),
          value: 'renewal_requests_pending',
          width: 150,
          sortable: true,
          align: 'center'
        },

        {
          text: this.$t('statistics.renewal_requests_accepted'),
          value: 'renewal_requests_accepted',
          width: 150,
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('statistics.renewal_requests_rejected'),
          value: 'renewal_requests_rejected',
          width: 150,
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('statistics.schools_not_requested_renewal'),
          value: 'schools_not_requested_renewal',
          width: 170,
          sortable: true,
          align: 'center'
        }
      ];
    },

    schoolHeaders() {
      return [
        {
          text: this.$t('schools.schoolName'),
          value: 'name',
          width: 200,
          sortable: true
        },
        {
          text: this.$t('schools.domain'),
          value: 'domain',
          width: 150,
          sortable: true
        },
        {
          text: this.$t('schools.domain_scid'),
          value: 'scid',
          width: 120,
          sortable: true
        },
        {
          text: this.$t('directorates.governorate'),
          value: 'directorate.governorate.name',
          width: 120,
          sortable: true
        },
        {
          text: this.$t('directorates.directorate'),
          value: 'directorate.name',
          width: 150,
          sortable: true
        },
        {
          text: this.$t('schools.owner'),
          value: 'owner',
          width: 120,
          sortable: true
        },
        {
          text: this.$t('schools.manager_name'),
          value: 'manager_name',
          width: 150,
          sortable: true
        },
        {
          text: this.$t('schools.manager_phone'),
          value: 'manager_phone',
          width: 150,
          sortable: true
        },
      ];
    },

    exportData() {
      return this.comparisonDataWithTotals.map(item => ({
        governorate_name: item.governorate_name,
        directorate_name: item.directorate_name,
        schools_in_old_year: item.statistics.schools_in_old_year,
        schools_in_new_year: item.statistics.schools_in_new_year,
        schools_in_new_year_percentage: this.formatPercentage(item.percentages.schools_in_new_year),
        new_schools_count: item.statistics.new_schools_count,
        new_schools_percentage: this.formatPercentage(item.percentages.new_schools_count),
        renewed_schools: item.statistics.renewed_schools,
        renewed_schools_percentage: this.formatPercentage(item.percentages.renewed_schools),
        renewal_requests_pending: item.statistics.renewal_requests_pending,
        renewal_requests_pending_percentage: this.formatPercentage(item.percentages.renewal_requests_pending),
        renewal_requests_accepted: item.statistics.renewal_requests_accepted,
        renewal_requests_accepted_percentage: this.formatPercentage(item.percentages.renewal_requests_accepted),
        renewal_requests_rejected: item.statistics.renewal_requests_rejected,
        renewal_requests_rejected_percentage: this.formatPercentage(item.percentages.renewal_requests_rejected),
        schools_not_requested_renewal: item.statistics.schools_not_requested_renewal,
        schools_not_requested_renewal_percentage: this.formatPercentage(item.percentages.schools_not_requested_renewal)
      }));
    },

    exportColumns() {
      return [
        { label: this.$t('directorates.governorate'), field: 'governorate_name' },
        { label: this.$t('directorates.directorate'), field: 'directorate_name' },
        { label: this.$t('statistics.schools_in_old_year'), field: 'schools_in_old_year' },
        { label: this.$t('statistics.schools_in_new_year'), field: 'schools_in_new_year' },
        { label: this.$t('statistics.schools_in_new_year') + ' %', field: 'schools_in_new_year_percentage' },
        { label: this.$t('statistics.new_schools'), field: 'new_schools_count' },
        { label: this.$t('statistics.new_schools') + ' %', field: 'new_schools_percentage' },
        { label: this.$t('statistics.renewed_schools'), field: 'renewed_schools' },
        { label: this.$t('statistics.renewed_schools') + ' %', field: 'renewed_schools_percentage' },
        { label: this.$t('statistics.renewal_requests_pending'), field: 'renewal_requests_pending' },
        { label: this.$t('statistics.renewal_requests_pending') + ' %', field: 'renewal_requests_pending_percentage' },
        { label: this.$t('statistics.renewal_requests_accepted'), field: 'renewal_requests_accepted' },
        { label: this.$t('statistics.renewal_requests_accepted') + ' %', field: 'renewal_requests_accepted_percentage' },
        { label: this.$t('statistics.renewal_requests_rejected'), field: 'renewal_requests_rejected' },
        { label: this.$t('statistics.renewal_requests_rejected') + ' %', field: 'renewal_requests_rejected_percentage' },
        { label: this.$t('statistics.schools_not_requested_renewal'), field: 'schools_not_requested_renewal' },
        { label: this.$t('statistics.schools_not_requested_renewal') + ' %', field: 'schools_not_requested_renewal_percentage' }
      ];
    },

    schoolExportColumns() {
      return [
        { label: this.$t('schools.name'), field: 'name' },
        { label: this.$t('schools.domain'), field: 'domain' },
        { label: this.$t('schools.scid'), field: 'scid' },
        { label: this.$t('directorates.governorate'), field: 'directorate.governorate.name' },
        { label: this.$t('directorates.directorate'), field: 'directorate.name' },
        { label: this.$t('schools.owner'), field: 'owner' },
        { label: this.$t('schools.manager_name'), field: 'manager_name' },
        { label: this.$t('schools.manager_phone'), field: 'manager_phone' }
      ];
    }
  },
  created() {
    this.loadYears();
  },
  methods: {
    async loadYears() {
      try {
        this.years = await this.$store.dispatch('GetYears');
        this.selectedOldYear = this.years[1] ? this.years[1].id : null;
        this.selectedNewYear = this.years[0] ? this.years[0].id : null;
        this.loadComparison()
      } catch (error) {
        console.error('Error loading years:', error);
        this.$toast.error(this.$t('AnErrorOccurredDuringTheProcess'));
      }
    },

    async onYearChange() {
      if (this.selectedOldYear && this.selectedNewYear) {
        if (this.selectedOldYear === this.selectedNewYear) {
          this.$toast.warning(this.$t('statistics.same_year_selected'));
          this.selectedNewYear = null;
          return;
        }

        // Check if new year is greater than old year
        const oldYear = this.years.find(y => y.id === this.selectedOldYear);
        const newYear = this.years.find(y => y.id === this.selectedNewYear);

        if (oldYear && newYear && newYear.school_year <= oldYear.school_year) {
          this.$toast.warning(this.$t('statistics.new_year_must_be_greater'));
          this.selectedNewYear = null;
          return;
        }

        await this.loadComparison();
      }
    },

    async loadComparison() {
      this.loading = true;
      try {
        const response = await this.$axios.get('/statistics/year-comparison', {
          params: {
            year_id_old: this.selectedOldYear,
            year_id_new: this.selectedNewYear
          }
        });

        if (response.data.status === 'Successful') {
          this.comparisonData = response.data.data;
        }
      } catch (error) {
        console.error('Error loading comparison:', error);
        this.$toast.error(this.$t('AnErrorOccurredDuringTheProcess'));
      } finally {
        this.loading = false;
      }
    },

    calculatePercentages(statistics) {
      const baseValue = statistics.schools_in_old_year || 0;

      return {
        schools_in_new_year: baseValue ? (statistics.schools_in_new_year / baseValue) * 100 : 0,
        new_schools_count: baseValue ? (statistics.new_schools_count / baseValue) * 100 : 0,
        renewed_schools: baseValue ? (statistics.renewed_schools / baseValue) * 100 : 0,
        renewal_requests_pending: baseValue ? (statistics.renewal_requests_pending / baseValue) * 100 : 0,
        renewal_requests_accepted: baseValue ? (statistics.renewal_requests_accepted / baseValue) * 100 : 0,
        renewal_requests_rejected: baseValue ? (statistics.renewal_requests_rejected / baseValue) * 100 : 0,
        schools_not_requested_renewal: baseValue ? (statistics.schools_not_requested_renewal / baseValue) * 100 : 0
      };
    },

    calculateTotals(data) {
      return data.reduce((totals, item) => {
        return {
          schools_in_old_year: totals.schools_in_old_year + item.statistics.schools_in_old_year,
          schools_in_new_year: totals.schools_in_new_year + item.statistics.schools_in_new_year,
          new_schools_count: totals.new_schools_count + item.statistics.new_schools_count,
          renewal_requests_pending: totals.renewal_requests_pending + item.statistics.renewal_requests_pending,
          renewal_requests_accepted: totals.renewal_requests_accepted + item.statistics.renewal_requests_accepted,
          renewed_schools: totals.renewed_schools + item.statistics.renewed_schools,
          renewal_requests_rejected: totals.renewal_requests_rejected + item.statistics.renewal_requests_rejected,
          schools_not_requested_renewal: totals.schools_not_requested_renewal + item.statistics.schools_not_requested_renewal
        };
      }, {
        schools_in_old_year: 0,
        schools_in_new_year: 0,
        new_schools_count: 0,
        renewal_requests_pending: 0,
        renewal_requests_accepted: 0,
        renewed_schools: 0,
        renewal_requests_rejected: 0,
        schools_not_requested_renewal: 0
      });
    },

    formatPercentage(value) {
      if (!value || isNaN(value)) return '0%';
      return `${value.toFixed(0)}%`;
    },

    getPercentageClass(value) {
      if (value > 100) return 'green--text';
      if (value < 100) return 'red--text';
      return 'grey--text';
    },

    getPercentageChipColor(value) {
      if (value > 100) return 'success';
      if (value < 100) return 'error';
      return 'grey';
    },

    async openDialog(statisticType, directorate) {

      this.currentDialogTitle = `${this.$t(`statistics.${statisticType}`)} - ${directorate.directorate_name ?? 'جميع المديريات'}`;
      this.currentDirectorate = directorate;
      this.schoolsDialog = true;
      this.dialogLoading = true;
      this.currentSchoolsData = [];

      try {
        const response = await this.$axios.get('/statistics/year-comparison', {
          params: {
            year_id_old: this.selectedOldYear,
            year_id_new: this.selectedNewYear,
            list: statisticType,
            directorate_id: directorate.isTotal ? null : directorate.directorate_id
          }
        });

        if (response.data.status === 'Successful') {
          this.currentSchoolsData = response.data.data;
        }
      } catch (error) {
        console.error('Error loading schools data:', error);
        this.$toast.error(this.$t('AnErrorOccurredDuringTheProcess'));
      } finally {
        this.dialogLoading = false;
      }
    },

    viewSchool(item) {
      this.$router.push({
        name: 'schools.view',
        params: { id: item.id }
      });
    }
  }
};
</script>

<style scoped>
.clickable-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-chip:hover {
  transform: scale(1.05);
}

.bordered >>> td {
  border-left: rgba(0, 0, 0, .12) 1px solid;
}

.bordered >>> table thead .fixed {
  position: sticky;
  right: 0;
  z-index: 2;
  background-color: white;
}

.bordered >>> table tbody .fixed {
  position: sticky;
  right: 0;
  z-index: 2;
  background-color: white;
}

.bordered >>> tbody tr:first-child {
  background-color: #f5f5f5;
  font-weight: bold;
}

.bordered >>> tbody tr:first-child:hover {
  background-color: #f5f5f5 !important;
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 4px;
}

.text-caption {
  font-size: 0.75rem !important;
  line-height: 1.2;
}
</style>
